import { formatCurrencyFromString } from '@sales-portal/blocs/utils/money';
import { isValidNumber } from '@/utils/number';
import {
  PriceEditDisplayInfoDetailFieldsFragment,
  PriceEditDisplayInfoTrend,
} from '@/types';

export interface PriceEditDisplayCell {
  // Only way left to tell a money column from a plain one.
  currency: string | null;
  display: string | null;
  isPercent: boolean;
  raw: number | string | null;
  trend: PriceEditDisplayInfoTrend | null;
}

// Strips % and commas so e.g. "10.4%" parses as a number, not text.
const parseNumericValue = (
  value: string,
): { isPercent: boolean; value: number } | null => {
  const stripped = value.replace(/[%,\s]/g, '');
  if (stripped === '') {
    return null;
  }
  const parsed = Number(stripped);
  return isValidNumber(parsed)
    ? { isPercent: value.trimEnd().endsWith('%'), value: parsed }
    : null;
};

export const toPriceEditDisplayCell = (entry: {
  currency?: string | null;
  trend?: PriceEditDisplayInfoTrend | null;
  value?: string | null;
  value_display?: string | null;
}): PriceEditDisplayCell => {
  const isPrice = !!entry.currency;
  const display = entry.value
    ? isPrice
      ? entry.value_display ||
        formatCurrencyFromString(entry.value, entry.currency)
      : entry.value
    : null;

  // raw: numbers stay numbers, text stays text, bad currency values become null.
  let raw: number | string | null = null;
  let isPercent = false;
  if (entry.value) {
    const parsedNumber = parseNumericValue(entry.value);
    if (parsedNumber) {
      raw = parsedNumber.value;
      isPercent = parsedNumber.isPercent;
    } else if (!isPrice) {
      raw = entry.value;
    }
  }
  const trend = entry.trend
    ? (entry.trend.toUpperCase() as PriceEditDisplayInfoTrend)
    : null;
  return { currency: entry.currency ?? null, display, isPercent, raw, trend };
};

// Keys match PriceEditDisplayHeader's `${type}.${title}`, so columns can look up cells by header.key.
export const toPriceEditDisplayMap = (
  info: PriceEditDisplayInfoDetailFieldsFragment[] | null | undefined,
): Record<string, PriceEditDisplayCell> => {
  if (!info) {
    return {};
  }
  return info.reduce<Record<string, PriceEditDisplayCell>>((acc, entry) => {
    acc[`${entry.type}.${entry.title}`] = toPriceEditDisplayCell(entry);
    return acc;
  }, {});
};
